import { useCallback, useSyncExternalStore } from "react";

import type { PageFormat } from "./format";

/**
 * The reader's experimentation switch (shell control): render every page as markdown-it, as AstroMark, or `auto` (each
 * page's own format). Kept per browser in localStorage; `auto` when unset or when storage is unavailable.
 */
export type RenderMode = "auto" | PageFormat;

export const RENDER_MODES: readonly RenderMode[] = ["auto", "markdown-it", "astromark"];

/** The localStorage key. */
export const RENDER_MODE_STORAGE_KEY = "ark.renderMode";

/** Dispatched on `window` by `setRenderMode`; `detail.mode` is the new mode. */
export const RENDER_MODE_EVENT = "ark:render-mode";

/** Used when localStorage throws (private mode, blocked storage, quota), so the switch still works until reload. */
let memoryMode: RenderMode = "auto";
/** Set once a write failed: reads then come from memory, or the old stored value would win over the switch. */
let storageBroken = false;

/** A stored value as a mode; anything unknown is `auto`. */
export function parseRenderMode(value: unknown): RenderMode {
    return value === "markdown-it" || value === "astromark" ? value : "auto";
}

function storage(): Storage | null {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch {
        return null;
    }
}

/** The stored mode; `auto` when unset, unknown or unreadable. */
export function getRenderMode(): RenderMode {
    try {
        const store = storage();
        if (!store || storageBroken) return memoryMode;
        const value = store.getItem(RENDER_MODE_STORAGE_KEY);
        return value === null ? "auto" : parseRenderMode(value);
    } catch {
        return memoryMode;
    }
}

/** Stores the mode (`auto` clears the key) and tells every open page through {@link RENDER_MODE_EVENT}. */
export function setRenderMode(mode: RenderMode): void {
    const next = parseRenderMode(mode);
    memoryMode = next;
    try {
        const store = storage();
        if (next === "auto") store?.removeItem(RENDER_MODE_STORAGE_KEY);
        else store?.setItem(RENDER_MODE_STORAGE_KEY, next);
        storageBroken = false;
    } catch {
        storageBroken = true; // the in-memory value carries the switch until reload
    }
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(RENDER_MODE_EVENT, { detail: { mode: next } }));
}

/** Calls `listener` when the mode changes in this tab (`setRenderMode`) or in another tab (the `storage` event). */
export function subscribeRenderMode(listener: () => void): () => void {
    if (typeof window === "undefined") return () => {};
    const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === RENDER_MODE_STORAGE_KEY) listener();
    };
    window.addEventListener(RENDER_MODE_EVENT, listener);
    window.addEventListener("storage", onStorage);
    return () => {
        window.removeEventListener(RENDER_MODE_EVENT, listener);
        window.removeEventListener("storage", onStorage);
    };
}

/** `[mode, setMode]`, re-rendering whenever the mode changes anywhere. For the shell control and the reader. */
export function useRenderMode(): [RenderMode, (mode: RenderMode) => void] {
    const mode = useSyncExternalStore(subscribeRenderMode, getRenderMode, () => "auto" as RenderMode);
    const setMode = useCallback((next: RenderMode) => setRenderMode(next), []);
    return [mode, setMode];
}
